import { Routes } from '@angular/router';
import { PasswordGuard } from './_avmin/guards/password/password.guard';
import { SCOPES, ROLES } from './app.constants';
import { ACCOUNT_ROUTES } from './modules/account/account.routes';
import { AIRCRAFT_ROUTES } from './modules/aircraft/aircraft.routes';
import { AUTH_ROUTES } from './modules/auth/auth.routes';
import { AuthGuard } from './modules/auth/services/auth.guard';
import { ScopeGuard } from './modules/auth/services/scope.guard';
import { BUSINESS_ROUTES } from './modules/business/business.routes';
import { COHORT_ROUTES, COURSE_INTAKE_ROUTES } from './modules/course-intakes/course-intakes.routes';
import { COURSE_ROUTES } from './modules/courses/course.routes';
import { DEVELOPER_ROUTES } from './modules/developer/developer.routes';
import { ERROR_ROUTES } from './modules/errors/errors.routes';
import { EVENT_ROUTES } from './modules/events/events.module';
import { ITEM_ROUTES } from './modules/items/items.module';
import { MAIL_LIST_ROUTES } from './modules/mail-lists/mail-lists.routes';
import { PIPELINE_ROUTES } from './modules/pipelines/pipeline.routes';
import { POS_ROUTES } from './modules/pos/pos.routes';
import { QUALIFICATIONS_ROUTES } from './modules/qualifications/qualifications.routes';
import { REPORT_ROUTES } from './modules/reports/report.routes';
import { SALES_ROUTES } from './modules/sales/sales.routes';
import { SETTINGS_ROUTES } from './modules/settings/settings.routes';
import { STUDENT_RECORDS_ROUTES } from './modules/student-records/student-records.routes';
import { USER_ROUTES } from './modules/users/users.routes';
import { DASHBOARD_ROUTES } from './pages/dashboard/dashboard.routes';
import { ROSTER_ROUTES } from './modules/roster/roster.routes';
import { RESOURCES_ROUTES } from './modules/resources/resource.routes';
import { SCHEDULE_ROUTES } from './modules/schedule/schedule.routes';
import { BOOKING_ROUTES } from './modules/bookings/booking.routes';
import { ON_TIME_PERFORMANCE_ROUTES } from './modules/on-time-performance/on-time-performance.routes';
import { OPS_MANAGEMENT_ROUTES } from './modules/ops-management/ops-management.routes';

export const APP_ROUTES: Routes = [
  { path: 'auth', children: AUTH_ROUTES },
  { path: 'error', children: ERROR_ROUTES },
  {
    path: '',
    canActivate: [AuthGuard],
    loadComponent: () => import('./_layout/layout.component').then((m) => m.LayoutComponent),
    children: [
      { path: 'account', children: ACCOUNT_ROUTES },
      {
        path: 'aircraft',
        canActivate: [ScopeGuard],
        data: { scopes: [SCOPES.aircraft.read] },
        children: AIRCRAFT_ROUTES,
      },
      {
        path: 'bookings',
        canActivate: [ScopeGuard],
        data: { scopes: [SCOPES.bookings.write, SCOPES.bookings.manage] },
        children: BOOKING_ROUTES,
      },
      { path: 'courses', canActivate: [ScopeGuard], data: { scopes: [SCOPES.courses.read] }, children: COURSE_ROUTES },
      {
        path: 'cohorts',
        canActivate: [ScopeGuard],
        data: { scopes: [SCOPES.courses.intakes.read] },
        children: COHORT_ROUTES,
      },
      {
        path: 'course-intakes',
        canActivate: [ScopeGuard],
        data: { scopes: [SCOPES.courses.intakes.read] },
        children: COURSE_INTAKE_ROUTES,
      },
      { path: 'dashboard', children: DASHBOARD_ROUTES },
      {
        path: 'dev',
        canActivate: [ScopeGuard],
        data: { scopes: [ROLES.systemAdministrators] },
        children: DEVELOPER_ROUTES,
      },
      {
        path: 'businesses',
        canActivate: [ScopeGuard],
        data: { scopes: [SCOPES.businesses.manage] },
        children: BUSINESS_ROUTES,
      },
      { path: 'events', canActivate: [ScopeGuard], data: { scopes: [SCOPES.events.read] }, children: EVENT_ROUTES },
      {
        path: 'mail-lists',
        canActivate: [ScopeGuard],
        data: { scopes: [SCOPES.mailList.manage] },
        children: MAIL_LIST_ROUTES,
      },
      {
        path: 'pipelines',
        canActivate: [ScopeGuard],
        data: { scopes: [SCOPES.pipelines.view] },
        children: PIPELINE_ROUTES,
      },
      { path: 'pos', canActivate: [ScopeGuard], data: { scopes: [SCOPES.sales.create] }, children: POS_ROUTES },
      { path: 'products', canActivate: [ScopeGuard], data: { scopes: [SCOPES.items.read] }, children: ITEM_ROUTES },
      {
        path: 'qualifications',
        canActivate: [ScopeGuard],
        data: { scopes: [SCOPES.admin.qualifications.read] },
        children: QUALIFICATIONS_ROUTES,
      },
      { path: 'users', canActivate: [ScopeGuard], data: { scopes: [SCOPES.admin.users.read] }, children: USER_ROUTES },
      { path: 'reports', canActivate: [ScopeGuard], data: { scopes: [SCOPES.reports.read] }, children: REPORT_ROUTES },
      {
        path: 'resources',
        canActivate: [ScopeGuard],
        data: { scopes: [SCOPES.resources.read] },
        children: RESOURCES_ROUTES,
      },
      {
        path: 'roster',
        canActivate: [ScopeGuard],
        data: { scopes: [SCOPES.admin.roster.read, SCOPES.admin.roles.manage] },
        children: ROSTER_ROUTES,
      },
      {
        path: 'schedule',
        canActivate: [ScopeGuard],
        data: { scopes: [SCOPES.bookings.read] },
        children: SCHEDULE_ROUTES,
      },
      {
        path: 'on-time-performance',
        canActivate: [ScopeGuard],
        data: { scopes: [SCOPES.bookings.read] },
        children: ON_TIME_PERFORMANCE_ROUTES,
      },
      {
        path: 'ops-management',
        canActivate: [ScopeGuard],
        data: { scopes: [SCOPES.studentRecords.read] },
        children: OPS_MANAGEMENT_ROUTES,
      },
      { path: 'sales', canActivate: [ScopeGuard], data: { scopes: [SCOPES.sales.view] }, children: SALES_ROUTES },
      { path: 'settings', children: SETTINGS_ROUTES },
      {
        path: 'students',
        canActivate: [ScopeGuard],
        data: { scopes: [SCOPES.admin.users.read] },
        children: STUDENT_RECORDS_ROUTES,
      },
      {
        path: 'tutor-time/log',
        loadComponent: () =>
          import('./modules/events/components/tutor-event-form/tutor-event-form.component').then(
            (m) => m.TutorEventFormComponent
          ),
        canActivate: [ScopeGuard, PasswordGuard],
        data: { scopes: [SCOPES.events.manage] },
      },
      { path: '', redirectTo: '/dashboard', pathMatch: 'full' },
      { path: '**', redirectTo: 'error/404' },
    ],
  },
  { path: '**', redirectTo: 'error/404' },
];
